import { useCallback } from 'react';
import {
  useStartEmailContent,
  useDeleteEmailContent,
  type EmailDocumentType,
  type EmailContentResponse,
} from 'src/meeting-note/hooks/useGenerateEmailContent';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

export const useEmailDocument = () => {
  const { clientId } = useCurrentClient();
  const { startEmailContent, isGenerating } = useStartEmailContent();
  const { deleteEmailContent, isDeleting } = useDeleteEmailContent();

  const handleEmailClick = useCallback(
    async (
      documentId: string,
      documentType: EmailDocumentType,
    ): Promise<EmailContentResponse | null> => {
      try {
        const response = await startEmailContent({ clientId, documentId, documentType });
        if (!response?.id) throw new Error('Failed to generate email content');

        return response;
      } catch (e) {
        notifyAndLogError(
          'Failed to generate email',
          'An error occurred while generating email content. Please try again later.',
          e,
          { clientId, documentId, documentType },
        );
        return null;
      }
    },
    [clientId, startEmailContent],
  );

  const handleDeleteEmail = useCallback(
    async (emailContentId: string) => {
      try {
        await deleteEmailContent(emailContentId);
      } catch {
        // Best-effort cleanup
      }
    },
    [deleteEmailContent],
  );

  return {
    handleEmailClick,
    handleDeleteEmail,
    isGenerating,
    isDeleting,
  };
};
